import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Product } from '../../stores/appReportsStore'
import { calculateTotalSpent } from '../../services/import/importProductIdsService'
import { currencyFormatter } from '../../services/helperService'
import { cardResponsiveWidth, productCardClass } from '../../styling/stylingHelper'
import { SupplierLogo } from '../ui/SupplierLogo'


interface ProductCardProps {
  product: Product
}

export default function ProductCard({ product }: ProductCardProps) {
    const { t } = useTranslation();
    const [imgSrc, setImgSrc] = useState(product.src)

    const totalPrice = calculateTotalSpent(product).toFixed(2)
  const stdAmount = product.useCombo
    ? product.combo?.standardizedAmount ?? product.standardizedAmount
    : product.standardizedAmount
  const isGrammOrMl = ['g', 'ml'].includes(product.standardUnit)
  const totalQuantity = isGrammOrMl
    ? (product.quantity * stdAmount) / 1000
    : product.quantity * stdAmount

  const qty = new Intl.NumberFormat(undefined,{
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
    }).format(totalQuantity);
  const stdUnit =
    product.standardUnit === 'g'
      ? 'kg'
      : product.standardUnit === 'ml'
      ? 'ltr'
      : product.standardUnit
  const unitInfo = `${currencyFormatter.format(product.unitPrice)}/${stdUnit}`

  return (
    <div className={`${productCardClass} flex flex-col md:flex-row flex-none shrink-0 ${cardResponsiveWidth} p-3`}>
      {/* Top: image + info */}
      <div className="flex w-full">
        {/* Image */}
        <img
          src={imgSrc}
          alt={product.name}
          className="w-[70px] h-[70px] object-cover rounded-xl my-auto border-2 border-white dark:border-card-dark"
          onError={e => {
            e.currentTarget.onerror = null
            setImgSrc('/no_photo.png')
          }}
        />
  
        {/* Info */}
        <div className="flex-1 flex flex-col justify-between ml-2 min-w-0">
          {/* Qty/Name (+ mobile brand/logo) + desktop price/total */}
          <div className="flex items-stretch justify-between">
            {/* Qty + Name */}
            <div className="flex-1 flex flex-col min-w-0">
                <div className="flex items-start">
                    <div className="w-fit text-xs font-medium rounded-lg bg-textBlue/10 dark:bg-textBlue-dark/10 px-2 py-0.5 text-textBlue dark:text-textBlue-dark mb-1">
                        {qty}{stdUnit}
                    </div>
                    <div className="w-fit text-xs font-medium rounded-lg bg-borderGrey dark:bg-borderGrey-dark px-2 py-0.5 text-tertiary dark:text-tertiary-dark mb-1 block ml-2">
                        ID: {product.articleId}
                    </div>
                </div>
                <a
                    href={product.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline text-text dark:text-text-dark"
                >
                    <h4 className="truncate font-semibold" title={product.name}>
                    {product.name}
                    </h4>
                </a>
            </div>
  
            {/* Desktop-only total spent */}
            <div className="hidden md:flex self-center w-px h-[60%] bg-borderGrey dark:bg-borderGrey-dark my-1 mx-1.5" />
            <div className="hidden md:flex items-center">
              <span className="text-base text-textGreen dark:text-textGreen-dark font-bold min-w-[80px] text-right">
                {currencyFormatter.format(Number(totalPrice))}
              </span>
            </div>
          </div>
  
          {/* Desktop-only bottom row with brand/logo and unit price side by side */}
          <div className="flex items-center min-w-0">
            {/* Brand and platform logo - flex to take remaining width */}
            <div className="flex items-center flex-1 min-w-0">
              <span className="text-muted dark:text-muted-dark mr-1">
                {t('product.brand')}
              </span>
              <span className="text-secondary dark:text-secondary-dark font-medium truncate" title={product.brand}>
                {product.brand}
              </span>
              <span className="text-muted dark:text-muted-dark mx-1 font-semibold">
                ·
              </span>
              <SupplierLogo
                platform={product.platform}
                alt={product.platform}
                size="small"
                className="inline-block align-middle"
              />
            </div>

            <div className="hidden md:flex justify-end ml-2">
              <span className="text-primary dark:text-primary-dark font-semibold">
                {unitInfo}
              </span>
            </div>
          </div>
        </div>
      </div>
  
      {/* Mobile-only price / total row below image + info */}
      <div className="flex justify-between mt-2 md:hidden px-2">
        <div className="flex flex-col">
          <span className="text-sm text-muted dark:text-muted-dark">
            {t('product.price')}
          </span>
          <span className="font-semibold">{unitInfo}</span>
        </div>
        <div className="flex items-center">
          <span className="text-base text-textGreen dark:text-textGreen-dark font-bold">
            {currencyFormatter.format(Number(totalPrice))}
          </span>
        </div>
      </div>
    </div>
  )
}
